import { Component, ViewChild, ElementRef, OnInit, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, Subscription } from 'rxjs';
import { LocationStrategy } from '@angular/common';

import { UrlService } from '@core/services/url/url.service';
import { CurrentSessionService }  from '@core/services/current-session/current-session.service';
import { File } from '@core/objects/file';
import { Note } from '@core/objects/note';
import { User } from '@core/objects/user';
// models for PDFjsConfig and options
import { PdfjsViewerAnnotationChanges, PdfjsViewerConfig } from './pdfjs-viewer';

// import WebViewer from '@pdftron/pdfjs-express';
import WebViewerInstance from '@pdftron/pdfjs-express';
// import Annotations  from '@pdftron/pdfjs-express';
import { PdfjsViewer } from './pdfjs-viewer';
// import WebViewerInstance from '@pdftron/pdfjs-express';

@Component({
  selector: 'core-pdfjs-viewer',
  templateUrl: './pdfjs-viewer.component.html',
  styleUrls: ['./pdfjs-viewer.component.scss']
})
export class PdfjsViewerComponent implements OnInit, OnDestroy, OnChanges {

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_NOT_FOUND = 2;

    @ViewChild('viewer', { static: false }) viewerElement: ElementRef;
    wvInstance: any;

    @Input() file: File;
    @Input() viewerUser: User = null;
    @Input() notesLegacy: Array<Note>;
    @Input() annotations: string;
    @Input() annotationsAllowed: boolean;


    @Output() onSaveAnnotations = new EventEmitter<PdfjsViewerAnnotationChanges>(); // TODO: change type to new notes interface

    public status: number = this.STATUS_LOADING;

    private retryDelay = 1000; // 1 second to get attempts of load pdf
    private viewerConfig: PdfjsViewerConfig;

    // private pdfWebViewerHanlder: WebViewer = null;
    protected docReadySubscriber: Subscription;
    protected annotChangeSubscriber: Subscription;
    // new
    private viewer: PdfjsViewer;
    private baseHref: string = '';

    constructor(
        private locationStrategy: LocationStrategy,
        public urlSrv: UrlService, 
        private http: HttpClient, 
        private currentSessionSrv: CurrentSessionService){}

    ngOnInit() {
        this.viewerConfig = { 
            menu: {annotations: this.annotationsAllowed}, 
            notes: {readonly : !this.annotationsAllowed}
        };
        this.baseHref = this.locationStrategy.getBaseHref();
        // console.debug(" ###DEBUG: current USER AND ORG!!!!",this.currentSessionSrv.getUser(),this.currentSessionSrv.getOrganization());
        // build the url to get the file preview pdf
        let fileUrl = this.urlSrv.build('/files/'+this.file.id+'/pdf');
        this.loadPDF(fileUrl);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes.annotations && this.viewer != null && changes.annotations.currentValue != ''){
            // load xfdf annotations again when thre was an update error (we delete new ones as well)
            this.viewer.importXfdfAnnotations(this.annotations);
        }
        if(changes.notesLegacy && this.viewer != null){
            // here we import old notes
            this.viewer.importLegacyNotes(this.notesLegacy);
        }
        if(changes.annotationsAllowed && !changes.annotationsAllowed.isFirstChange()){
            // here we import old notes
            this.viewerConfig = { 
                menu: {annotations: this.annotationsAllowed}, 
                notes: {readonly : !this.annotationsAllowed}
            };
        }
    }

    ngOnDestroy(): void {
        if(this.viewer)
            this.viewer.clear();
        if(this.docReadySubscriber)  // need to be checked in case document not load properly
            this.docReadySubscriber.unsubscribe();
        if(this.annotChangeSubscriber)
            this.annotChangeSubscriber.unsubscribe();
    }

    /**
     *
     * @param url the resource of pdf preview
     *
     * this initialize the PDFJS webviewer only after status is checked and is 200 ok.
     *
     */
    initializeWebViewer(url: string): void {
        // check user was given or we have it in the session (20/3/2024 for signatures <diego@twonas.com>) 
        let pdfUser = this.viewerUser === null ? this.currentSessionSrv.getUser() : this.viewerUser;
        // Initialize PDF.js Express viewer with the file url given and selecte user
        // let baseUrl = "/app/assets/pdfjs/";
        this.viewer = new PdfjsViewer(this.viewerConfig, pdfUser, this.baseHref, this.http);

        // set subscriptors
        this.docReadySubscriber =  this.viewer.onDocumentReady.subscribe((instance: WebViewerInstance) => {
            console.log('document ready!');
            // check what notes we have to import.
            if( this.annotations != null && this.annotations != '' && this.viewer){
                // load xfdf annotations
                this.viewer.importXfdfAnnotations(this.annotations);
            } else {
                // import legacy notes and save as xfdf annotations
                this.viewer.importLegacyNotes(this.notesLegacy);
            }
        });

        this.annotChangeSubscriber = this.viewer.onAnnotationsChange.subscribe((changes: PdfjsViewerAnnotationChanges) => {
            if(this.annotationsAllowed && !this.viewerConfig.notes?.readonly){ // save only when user has permision to annotate
                console.log('xfdf changed --->', changes);
                this.onSaveAnnotations.emit(changes);
            }
        });

        this.viewer.init(url, this.viewerElement);
        
    }



    // custom delay to perform reload of pdf with a new attempt of httpClient head() call
    protected delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }


    /**
     *
     * @param url the built url form the resource pdf preview "baseurl/file/{fileid}/pdf"
     * @returns the response status code from the HEAD call to the preview pdf url
     *
     */
    protected async checkPDFStatus(url: string): Promise<number> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/pdf' });
        // Return the status of head() response
        try {
            const response = await firstValueFrom(this.http.head(url, { headers, observe: 'response' }));
            return response.status;
        } catch (error) {
            console.debug(' pdf preview error:', error);
            // in case we need to manage here
            return error.status; 
        }
    }

    /**
     *
     * @param url the built url form the resource pdf preview "baseurl/file/{fileid}/pdf"
     *
     * after check pdf status with the httpClient HEAD call, we manage the response status
     */
    protected async loadPDF(url: string): Promise<void> {
        const status = await this.checkPDFStatus(url);
        
        if (status === 200) {
            this.status = this.STATUS_DONE;
            this.initializeWebViewer(url);
        } else if (status === 425) {
            await this.delay(this.retryDelay); // Add a delay before retrying
            await this.loadPDF(url); // Call the function recursively to load 
            // TODO: maybe put number some retry limits.
        } else {
            // Handle the cases when the PDF is not available (404, 302, others);
            console.log('PDF preview not available ',status );
            // by now error not available
            this.status = this.STATUS_NOT_FOUND;
        }
    }

}
